import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axiosInstance from '../../../utils/axios';
import { Num } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../components/gps/Modal';
import ModalTicket from '../../../components/gps/ModalTicket';
import { linhasIniciais } from '../../../components/gps/paginacao';

/**
 * Tickets de suporte (substitui o antigo modulo de Mensagens).
 *
 * O que esta tela faz, e que a anterior de Mensagens nao fazia:
 *  - assunto separado da descricao (antes o assunto ia no campo "descricao")
 *  - categoria e prioridade, que definem o prazo de SLA
 *  - prazo exibido ANTES de salvar, calculado com os mesmos prazos que o backend
 *    grava - a tela nunca promete prazo diferente do que sera aplicado
 *  - farol de SLA e contadores por status
 *
 * TAB_NOTIFICACAO (o sininho do topo) NAO faz parte disto e segue intacta.
 *
 * ---------------------------------------------------------------------------
 * Migrada para o visual novo (shadcn + Tailwind) em 14/08/2026.
 *
 * DUAS CORRECOES DE COMPORTAMENTO junto da migracao:
 *
 * 1. PAGINACAO DE VERDADE. A versao anterior pedia `page=0&rows=50` fixo e
 *    mostrava `totalElements` no rodape. Com 50+ tickets, os demais eram
 *    inalcancaveis pela tela - existiam na contagem e nao na lista. Os
 *    parametros ja eram aceitos pelo endpoint; so nao eram usados.
 *
 * 2. O FAROL VIROU ROTULO, nao so bolinha. Um circulo colorido sem texto e
 *    ilegivel para quem nao distingue verde de vermelho, e essa e justamente a
 *    coluna que decide o que atender primeiro. Agora e ponto + palavra, e a
 *    ordem visual da linha continua a mesma.
 */

type Ticket = {
  codigo: number;
  numero: number;
  assunto: string;
  descricao: string;
  idCategoria: number;
  prioridade: string;
  status: string;
  prazoSla: string | null;
  slaVencido: boolean;
  farolSla: 'VERDE' | 'AMARELO' | 'VERMELHO';
  createdAt: string;
};

type Categoria = {
  codigo: number;
  nome: string;
  /** Prioridade com que a categoria e atendida - ver o comentario no passo. */
  prioridade?: string;
  prazos: Array<{ prioridade: string; prazoDiasUteis: number }>;
};

const PRIORIDADES = ['BAIXA', 'MEDIA', 'ALTA', 'URGENTE'];
const LINHAS_POR_PAGINA = linhasIniciais();

const STATUS_LABEL: Record<string, string> = {
  ABERTO: 'Aberto',
  EM_ATENDIMENTO: 'Em atendimento',
  RESPONDIDO: 'Respondido',
  FECHADO: 'Fechado',
};

const STATUS_TOM: Record<string, string> = {
  ABERTO: 'bg-info-soft text-info',
  EM_ATENDIMENTO: 'bg-warn-soft text-warn',
  RESPONDIDO: 'bg-ok-soft text-ok',
  FECHADO: 'bg-soft text-faint',
};

// O farol vem calculado pelo backend; aqui so se traduz para cor e palavra.
const FAROL: Record<string, { classe: string; rotulo: string }> = {
  VERDE: { classe: 'text-ok', rotulo: 'no prazo' },
  AMARELO: { classe: 'text-warn', rotulo: 'perto' },
  VERMELHO: { classe: 'text-danger', rotulo: 'crítico' },
};

const TOM_PRIORIDADE: Record<string, string> = {
  URGENTE: 'text-danger font-bold',
  ALTA: 'text-info font-semibold',
  MEDIA: 'text-muted-foreground',
  BAIXA: 'text-faint',
};

// Filtro de status: o valor vai direto no parametro `status` do endpoint, que
// aceita os nomes do enum mais os dois agregados. Indices numericos porque o
// FiltroSegmentado trabalha com numero; o mapa faz a traducao.
const FILTROS_STATUS = ['OCULTAR_FECHADOS', 'TODOS', 'ABERTO', 'EM_ATENDIMENTO', 'RESPONDIDO', 'FECHADO'];
const ROTULO_FILTRO: Record<string, string> = {
  OCULTAR_FECHADOS: 'Em andamento',
  TODOS: 'Todos',
  ...STATUS_LABEL,
};

TicketsPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(data);
}

export default function TicketsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [itens, setItens] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [contadores, setContadores] = useState({
    abertos: 0,
    emAtendimento: 0,
    respondidos: 0,
    fechados: 0,
    vencidos: 0,
  });

  /* Ticket aberto para atendimento. `null` = nenhum.
     Faltava exatamente isto: a lista mostrava tudo e nao havia como ABRIR um
     ticket - clicar na linha nao fazia nada, e as rotas de responder e mudar
     status ja existiam no backend sem nenhum chamador. */
  const [atendendo, setAtendendo] = useState<number | null>(null);

  /* `?ticket=N` abre o ticket (30/09/2026). As notificacoes das mensagens antigas,
     migradas para tickets, apontam para /dashboard/tickets?ticket=N. Reage a
     mudanca da URL, e nao so a montagem: clicar no sino ja estando em Tickets
     troca so a query. O parametro sai da URL ao abrir, para F5 nao reabrir. */
  const [parametros, setParametros] = useSearchParams();
  useEffect(() => {
    const codigo = Number(parametros.get('ticket'));
    if (!Number.isInteger(codigo) || codigo <= 0) return;
    setAtendendo(codigo);
    parametros.delete('ticket');
    setParametros(parametros, { replace: true });
  }, [parametros, setParametros]);

  const [indiceStatus, setIndiceStatus] = useState(0);
  const [somenteVencidos, setSomenteVencidos] = useState(false);
  const [recarregar, setRecarregar] = useState(0);

  // Abertura de ticket
  const [aberto, setAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [assunto, setAssunto] = useState('');
  const [descricao, setDescricao] = useState('');
  const [idCategoria, setIdCategoria] = useState<number | ''>('');

  const filtroStatus = FILTROS_STATUS[indiceStatus] ?? 'OCULTAR_FECHADOS';
  const filtroSla = somenteVencidos ? 'VENCIDO' : 'TODOS';

  const carregarLista = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      /* Contadores em paralelo, mas a falha DELES nao esconde a lista: com
         Promise.all um 500 em /contadores virava "Nao foi possivel carregar os
         tickets" com a lista respondendo normalmente. */
      const [lista, cont] = await Promise.all([
        axiosInstance.get<{ content: Ticket[]; totalElements: number }>(
          `tickets/paginado?page=${pagina}&rows=${LINHAS_POR_PAGINA}&status=${filtroStatus}&sla=${filtroSla}`
        ),
        axiosInstance.get<typeof contadores>('tickets/contadores').catch(() => null),
      ]);
      setItens(lista.data?.content ?? []);
      setTotal(lista.data?.totalElements ?? 0);
      if (cont?.data) setContadores(cont.data);
    } catch (e: any) {
      // Erro NAO e tratado como "lista vazia": era o vicio do modulo antigo, em
      // que um 403 aparecia como "nenhum registro" e o usuario concluia que nao
      // existiam dados quando na verdade nao tinha permissao.
      setErro(e?.response?.data?.message || 'Não foi possível carregar os tickets.');
      setItens([]);
      setTotal(0);
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtroStatus, filtroSla]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista, recarregar]);

  useEffect(() => {
    axiosInstance
      .get<Categoria[]>('tickets/categorias/listar')
      .then((res) => setCategorias(res.data ?? []))
      .catch(() => setCategorias([]));
  }, []);

  // Prazo exibido na tela de abertura. Vem das categorias ja carregadas, que
  // trazem os prazos junto - evita uma chamada por troca de prioridade.
  /* A PRIORIDADE VEM DA CATEGORIA (29/09/2026, a pedido).

     Havia um seletor de quatro botoes aqui. Duas coisas erradas nele: quem abre
     o chamado descreve o problema, nao negocia o prazo; e prioridade oferecida
     a quem pede tende a ser sempre a mais alta, o que esvazia a fila de
     urgencia.

     Agora sai da categoria escolhida, e o backend IGNORA o que a tela mandar -
     tirar o seletor daqui sem fechar o campo la deixaria a regra como sugestao.

     A tela nao esconde a prioridade: mostra qual e, junto do prazo. Quem abre
     ve a consequencia da escolha que fez, em vez de escolher o proprio prazo. */
  const categoriaEscolhida = useMemo(
    () => categorias.find((c) => Number(c.codigo) === Number(idCategoria)) ?? null,
    [categorias, idCategoria],
  );
  const prioridade = categoriaEscolhida?.prioridade || 'MEDIA';

  const prazoPrevisto = useMemo(() => {
    const prazo = categoriaEscolhida?.prazos?.find((p) => p.prioridade === prioridade);
    return prazo?.prazoDiasUteis ?? null;
  }, [categoriaEscolhida, prioridade]);

  const limparFormulario = () => {
    setAssunto('');
    setDescricao('');
    setIdCategoria('');
  };

  const abrirTicket = async () => {
    if (!assunto.trim() || !descricao.trim() || !idCategoria) {
      enqueueSnackbar('Preencha assunto, categoria e descrição.', { variant: 'warning' });
      return;
    }
    setEnviando(true);
    try {
      await axiosInstance.post('tickets', {
        assunto: assunto.trim(),
        descricao: descricao.trim(),
        idCategoria: Number(idCategoria),
        // `prioridade` nao vai mais: quem decide e a categoria, no servidor.
      });
      enqueueSnackbar('Ticket aberto com sucesso.', { variant: 'success' });
      setAberto(false);
      limparFormulario();
      setPagina(0);
      setRecarregar((v) => v + 1);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao abrir o ticket.', { variant: 'error' });
    } finally {
      setEnviando(false);
    }
  };

  const colunas: Array<Coluna<Ticket>> = useMemo(
    () => [
      {
        chave: 'sla',
      exportar: (t) => (t.farolSla === 'VERMELHO' ? 'crítico' : t.farolSla === 'AMARELO' ? 'perto' : 'no prazo'),
        rotulo: 'SLA',
        largura: '120px',
        esqueleto: 'w-16',
        render: (t) => {
          const f = FAROL[t.farolSla] ?? FAROL.VERDE;
          return (
            <span
              className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${
                t.slaVencido ? 'text-danger' : f.classe
              }`}
              title={t.slaVencido ? 'SLA vencido' : `Farol ${t.farolSla}`}
            >
              <span className="h-2 w-2 flex-none rounded-full bg-current" />
              {t.slaVencido ? 'vencido' : f.rotulo}
            </span>
          );
        },
      },
      {
        chave: 'numero',
      exportar: (t) => t.numero,
        rotulo: 'Nº',
        largura: '70px',
        esqueleto: 'w-8',
        render: (t) => <Num className="text-[12px] font-semibold">{t.numero}</Num>,
      },
      {
        chave: 'assunto',
      exportar: (t) => t.assunto ?? '',
        rotulo: 'Assunto',
        largura: 'minmax(240px,2.6fr)',
        esqueleto: 'w-52',
        render: (t) => (
          <span className="block truncate text-[12.5px] font-semibold" title={t.assunto}>
            {t.assunto}
          </span>
        ),
      },
      {
        chave: 'prioridade',
      exportar: (t) => t.prioridade ?? '',
        rotulo: 'Prioridade',
        largura: '120px',
        esqueleto: 'w-16',
        render: (t) => (
          <span className={`text-[12px] tracking-wide ${TOM_PRIORIDADE[t.prioridade] || 'text-muted-foreground'}`}>
            {t.prioridade}
          </span>
        ),
      },
      {
        chave: 'abertura',
      exportar: (t) => (t.createdAt ? new Date(t.createdAt).toLocaleString('pt-BR') : ''),
        rotulo: 'Aberto em',
        largura: '150px',
        esqueleto: 'w-24',
        render: (t) => <Num className="text-[12px] text-muted-foreground">{formatarData(t.createdAt)}</Num>,
      },
      {
        chave: 'prazo',
      exportar: (t) => (t.prazoSla ? new Date(t.prazoSla).toLocaleString('pt-BR') : ''),
        rotulo: 'Prazo SLA',
        largura: '150px',
        esqueleto: 'w-24',
        render: (t) => (
          <Num className={`text-[12px] ${t.slaVencido ? 'font-bold text-danger' : 'text-muted-foreground'}`}>
            {formatarData(t.prazoSla)}
          </Num>
        ),
      },
      {
        chave: 'status',
      exportar: (t) => t.status ?? '',
        rotulo: 'Status',
        largura: '150px',
        esqueleto: 'w-20',
        render: (t) => (
          <span
            className={`inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-bold tracking-wide ${
              STATUS_TOM[t.status] || 'bg-soft text-muted-foreground'
            }`}
          >
            {STATUS_LABEL[t.status] || t.status}
          </span>
        ),
      },
    ],
    []
  );

  const podeAbrir = Boolean(assunto.trim() && descricao.trim() && idCategoria);

  return (
    <Page title="Tickets">
      <PainelLista<Ticket>
        titulo="Tickets"
        descricao="O prazo de SLA é definido pela categoria mais a prioridade, em dias úteis. O farol é calculado pelo servidor — a tela só mostra."
        acao={
          <button
            type="button"
            onClick={() => setAberto(true)}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Abrir ticket
          </button>
        }
        /* "RESOLVIDOS" ESTAVA FALTANDO (29/09/2026, a pedido).

           O backend SEMPRE devolveu `fechados` em `/tickets/contadores`, e o
           estado local ja o guardava (`contadores.fechados`) - so nunca foi
           desenhado. A faixa mostrava quatro numeros de trabalho PENDENTE e
           nenhum de trabalho CONCLUIDO, entao um dia inteiro de atendimento
           nao aparecia em lugar nenhum.

           Fica por ultimo e em tom neutro de proposito: a faixa existe para
           dizer o que falta fazer, e resolvido e contexto, nao urgencia. */
        indicadores={[
          { rotulo: 'Abertos', valor: contadores.abertos, legenda: 'aguardando', tom: 'info' },
          { rotulo: 'Em atendimento', valor: contadores.emAtendimento, legenda: 'em curso', tom: 'neutro' },
          { rotulo: 'Respondidos', valor: contadores.respondidos, legenda: 'aguardam você', tom: 'ok' },
          { rotulo: 'SLA vencido', valor: contadores.vencidos, legenda: 'fora do prazo', tom: 'danger' },
          { rotulo: 'Resolvidos', valor: contadores.fechados, legenda: 'encerrados', tom: 'neutro' },
        ]}
        segmentos={FILTROS_STATUS.map((s, i) => ({ valor: i, rotulo: ROTULO_FILTRO[s] || s }))}
        segmentoAtual={indiceStatus}
        aoTrocarSegmento={(v) => {
          setPagina(0);
          setIndiceStatus(v);
        }}
        filtrosExtras={
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground">
            <input
              type="checkbox"
              checked={somenteVencidos}
              onChange={(e) => {
                setPagina(0);
                setSomenteVencidos(e.target.checked);
              }}
              className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            Somente SLA vencido
          </label>
        }
        nomeArquivo="tickets"
        colunas={colunas}
        linhas={itens}
        chaveLinha={(t) => String(t.codigo)}
        aoClicarLinha={(t) => t.codigo && setAtendendo(Number(t.codigo))}
        carregando={carregando}
        erro={erro ? `${erro} Isto é um erro, não significa que não existem tickets.` : ''}
        aoRecarregar={carregarLista}
        vazio="Nenhum ticket encontrado com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / LINHAS_POR_PAGINA))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="tickets"
      />

      <ModalTicket
        aberto={atendendo !== null}
        codigo={atendendo}
        onFechar={() => setAtendendo(null)}
        onMudou={() => setRecarregar((n) => n + 1)}
      />

      <Modal
        aberto={aberto}
        titulo="Abrir ticket"
        descricao="Categoria e prioridade definem o prazo — o número aparece antes de salvar."
        icone="+"
        onFechar={() => setAberto(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setAberto(false)} disabled={enviando}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={abrirTicket} disabled={enviando || !podeAbrir}>
              {enviando ? 'Enviando...' : 'Abrir ticket'}
            </BotaoPrimario>
          </>
        }
      >
        <Campo rotulo="Assunto" obrigatorio dica="Uma linha. A descrição vem abaixo.">
          <input
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
            maxLength={200}
            autoFocus
            className={classesEntrada}
          />
        </Campo>

        <Campo rotulo="Categoria" obrigatorio>
          <select
            value={idCategoria}
            onChange={(e) => setIdCategoria(e.target.value ? Number(e.target.value) : '')}
            className={classesEntrada}
          >
            <option value="">Selecione…</option>
            {categorias.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.nome}
              </option>
            ))}
          </select>
          {!categorias.length && (
            <span className="mt-1 block text-[11px] text-danger">
              Nenhuma categoria cadastrada — sem categoria não há prazo de SLA para calcular.
            </span>
          )}
        </Campo>

        <Campo rotulo="Descrição" obrigatorio dica="Diga o que aconteceu, em qual aparelho e desde quando.">
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-line bg-surface p-3 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary"
          />
        </Campo>

        {/* O prazo aparece ANTES de salvar, e sai dos mesmos prazos que o backend
            usa para gravar. Se a combinação escolhida não tem prazo cadastrado,
            isso é dito — em vez de mostrar um número inventado. */}
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          {idCategoria === '' ? (
            <span className="text-muted-foreground">Escolha a categoria para ver a prioridade e o prazo.</span>
          ) : prazoPrevisto === null ? (
            <span className="text-warn">
              <strong>{categoriaEscolhida?.nome}</strong> é atendida como{' '}
              <strong>{prioridade}</strong>, mas essa combinação não tem prazo cadastrado. O servidor
              aplicará o padrão dele.
            </span>
          ) : (
            <span className="text-muted-foreground">
              <strong className="text-ink">{categoriaEscolhida?.nome}</strong> é atendida como{' '}
              <span className={`font-bold ${TOM_PRIORIDADE[prioridade] || 'text-ink'}`}>{prioridade}</span>
              {' — prazo de '}
              <Num className="font-bold text-ink">{prazoPrevisto}</Num> dia(s) útil(eis), descontando
              fins de semana e feriados cadastrados.
            </span>
          )}
        </div>
      </Modal>
    </Page>
  );
}
